import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Legacy permission-module names → platform module keys used by hospital.enabled_modules.
// When a nav entry's `mod` isn't in this map, it stays visible by default (fallback).
const MOD_TO_PLATFORM = {
  patients: "patients",
  opd: "opd",
  ipd: "ipd",
  emergency: "emergency",
  appointments: "appointments",
  investigations: "investigations",
  ot: "ot",
  beds: "ipd",
  doctors: "doctor",
  commission: "doctor_commission",
  hrms: "hrms",
  billing: "billing",
  pharmacy: "pharmacy",
  reports: "reports",
  audit: "audit",
  whatsapp: "notifications",
  abdm: "abdm",
  pmjay: "pmjay",
  kiosk: "kiosk",
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        // /auth/me returns 401 when no session — expected on first load / logged-out state
        setUser(null);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("dhoms_token", data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout API call failed (clearing local session anyway):", err?.message || err);
    }
    localStorage.removeItem("dhoms_token");
    setUser(null);
  };

  // Module gating helper — platform admin sees everything; hospital users
  // see only what their hospital.enabled_modules whitelist contains.
  const isModuleEnabled = (mod) => {
    if (!user) return false;
    if (user.role === "platform_admin") return true;
    const enabled = user.hospital?.enabled_modules || [];
    if (!enabled.length) return true;   // legacy user without hospital → allow all
    const pm = MOD_TO_PLATFORM[mod] || mod;
    return enabled.includes(pm);
  };

  // Memoise so consumers don't re-render on every parent render.
  const value = useMemo(
    () => ({ user, checking, login, logout, isModuleEnabled }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, checking]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
